import type { HardRule } from '../../types';

/**
 * Hard Rules: Non-negotiable constraints that must never be violated
 * These are enforced strictly during timetable generation
 */
export const mockHardRules: HardRule[] = [
  // Teacher constraints
  {
    id: 'hr-1',
    name: 'No Teacher Double Booking',
    description: 'A teacher cannot teach two lessons simultaneously',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher',
        operator: 'unique_per_timeslot',
        value: null,
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-2',
    name: 'Teacher Availability',
    description: 'Teachers can only be assigned during their available hours',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher.availability',
        operator: 'must_match',
        value: 'available',
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-3',
    name: 'Teacher Maximum Hours',
    description: 'Teachers cannot exceed their contract hours per week',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher.weeklyHours',
        operator: 'less_than_or_equal',
        value: 'teacher.contractHours',
      },
    ],
    isActive: true,
    priority: 2,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Room constraints
  {
    id: 'hr-4',
    name: 'No Room Double Booking',
    description: 'A room cannot be used by two classes at the same time',
    category: 'room',
    conditions: [
      {
        field: 'room',
        operator: 'unique_per_timeslot',
        value: null,
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-5',
    name: 'Room Capacity Limit',
    description: 'Class size must not exceed room capacity',
    category: 'room',
    conditions: [
      {
        field: 'class.size',
        operator: 'less_than_or_equal',
        value: 'room.capacity',
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-6',
    name: 'Specialist Room Requirement',
    description: 'Science, DT, Art, Music, and PE must use appropriate specialist rooms',
    category: 'room',
    conditions: [
      {
        field: 'subject.requiresSpecialistRoom',
        operator: 'equals',
        value: true,
      },
      {
        field: 'room.type',
        operator: 'matches',
        value: 'subject.roomTypeRequired',
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Student/Class constraints
  {
    id: 'hr-7',
    name: 'No Student Group Double Booking',
    description: 'A class, set, or year group cannot have two lessons at the same time',
    category: 'student',
    conditions: [
      {
        field: 'studentGroup',
        operator: 'unique_per_timeslot',
        value: null,
      },
    ],
    isActive: true,
    priority: 1,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-8',
    name: 'Minimum Break Time',
    description: 'Students must have at least 20 minutes lunch break',
    category: 'student',
    conditions: [
      {
        field: 'period.type',
        operator: 'contains',
        value: 'lunch',
      },
      {
        field: 'period.duration',
        operator: 'greater_than_or_equal',
        value: 20,
      },
    ],
    isActive: true,
    priority: 2,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Curriculum constraints
  {
    id: 'hr-9',
    name: 'Core Curriculum Completeness',
    description: 'All required curriculum hours must be scheduled',
    category: 'curriculum',
    conditions: [
      {
        field: 'subject.totalScheduledHours',
        operator: 'greater_than_or_equal',
        value: 'subject.requiredHours',
      },
    ],
    isActive: true,
    priority: 2,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-10',
    name: 'Double Period Constraint',
    description: 'When double periods are required, they must be consecutive',
    category: 'curriculum',
    conditions: [
      {
        field: 'lesson.requiresDoubleLesson',
        operator: 'equals',
        value: true,
      },
      {
        field: 'lesson.followedBy',
        operator: 'is_consecutive',
        value: 'same_lesson',
      },
    ],
    isActive: true,
    priority: 2,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Timing constraints
  {
    id: 'hr-11',
    name: 'No PE Period 5',
    description: 'PE lessons cannot be scheduled in the last period (students need time to change)',
    category: 'timing',
    conditions: [
      {
        field: 'subject.code',
        operator: 'equals',
        value: 'PE',
      },
      {
        field: 'period.number',
        operator: 'not_equals',
        value: 5,
      },
    ],
    isActive: true,
    priority: 2,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'hr-12',
    name: 'Assembly Timing',
    description: 'Whole school assembly must be on Monday Period 1',
    category: 'timing',
    conditions: [
      {
        field: 'activity.type',
        operator: 'equals',
        value: 'assembly',
      },
      {
        field: 'day',
        operator: 'equals',
        value: 'monday',
      },
      {
        field: 'period.number',
        operator: 'equals',
        value: 1,
      },
    ],
    isActive: true,
    priority: 3,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
];

export const getHardRulesByCategory = (category: HardRule['category']): HardRule[] => {
  return mockHardRules.filter((rule) => rule.category === category);
};

export const getActiveHardRules = (): HardRule[] => {
  return mockHardRules.filter((rule) => rule.isActive);
};
